import React, { useEffect, useState } from 'react';
import {
  Page,
  Card,
  Layout,
  Text,
  BlockStack,
  InlineStack,
  Banner,
  Checkbox,
  Button,
  Spinner,
  Tooltip,
} from '@shopify/polaris';
import { fetchWithSessionToken } from '../lib/appBridge';
import { trackAnalyticsEvent } from '../lib/analytics';

interface SettingsPageProps {
  shop: string;
  host: string;
}

const SettingsPage: React.FC<SettingsPageProps> = ({ shop, host }) => {
  const [productSyncEnabled, setProductSyncEnabled] = useState(false);
  const [initialProductSyncEnabled, setInitialProductSyncEnabled] = useState(false);
  const [lastProductSyncAt, setLastProductSyncAt] = useState<string | null>(null);
  const [lastProductWebhookAt, setLastProductWebhookAt] = useState<string | null>(null);
  const [productSyncStatus, setProductSyncStatus] = useState('idle');
  const [productSyncError, setProductSyncError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadSettings() {
      try {
        setLoading(true);
        const res = await fetchWithSessionToken(host, '/api/product-sync-settings');
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data?.error || 'Failed to load product sync settings');
        }
        if (!cancelled) {
          setProductSyncEnabled(Boolean(data?.productSyncEnabled));
          setInitialProductSyncEnabled(Boolean(data?.productSyncEnabled));
          setProductSyncStatus(data?.productSyncStatus || 'idle');
          setLastProductSyncAt(data?.lastProductSyncAt || null);
          setLastProductWebhookAt(data?.lastProductWebhookAt || null);
          setProductSyncError(data?.productSyncError || null);
        }
      } catch (error: any) {
        if (!cancelled) {
          setProductSyncError(error?.message || 'Failed to load product sync settings');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadSettings();
    return () => {
      cancelled = true;
    };
  }, [host]);

  const handleSave = async () => {
    let attemptedProductSync = false;
    try {
      setSaving(true);
      setProductSyncError(null);
      trackAnalyticsEvent('settings_saved', {
        route: '/settings',
        shopDomain: shop,
      });
      if (productSyncEnabled !== initialProductSyncEnabled) {
        trackAnalyticsEvent('product_sync_preference_changed', {
          route: '/settings',
          shopDomain: shop,
          productSyncEnabled,
        });
      }
      const res = await fetchWithSessionToken(host, '/api/product-sync-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productSyncEnabled }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || 'Failed to save product sync settings');
      }
      setProductSyncEnabled(Boolean(data?.productSyncEnabled));
      setInitialProductSyncEnabled(Boolean(data?.productSyncEnabled));
      setProductSyncStatus(data?.productSyncStatus || 'idle');
      setLastProductSyncAt(data?.lastProductSyncAt || null);
      setLastProductWebhookAt(data?.lastProductWebhookAt || null);
      setProductSyncError(data?.productSyncError || null);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      if (productSyncEnabled && !initialProductSyncEnabled) {
        setSyncing(true);
        attemptedProductSync = true;
        trackAnalyticsEvent('product_sync_started', {
          route: '/settings',
          shopDomain: shop,
          source: 'settings',
        });
        const syncRes = await fetchWithSessionToken(host, '/api/sync-products', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ shop }),
        });
        const syncData = await syncRes.json().catch(() => ({}));
        if (!syncRes.ok) {
          throw new Error(syncData?.error || 'Failed to sync products');
        }
        trackAnalyticsEvent('product_sync_completed', {
          route: '/settings',
          shopDomain: shop,
          source: 'settings',
        });
      }
    } catch (error: any) {
      if (attemptedProductSync) {
        trackAnalyticsEvent('product_sync_failed', {
          route: '/settings',
          shopDomain: shop,
          source: 'settings',
        });
      }
      setProductSyncError(error?.message || 'Failed to save product sync settings');
    } finally {
      setSaving(false);
      setSyncing(false);
    }
  };

  return (
    <Page
      title="Einstellungen"
      primaryAction={{ content: 'Speichern', onAction: handleSave, loading: saving || syncing }}
    >
      <Layout>
        {saved && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setSaved(false)}>
              <p>Einstellungen wurden gespeichert</p>
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">
                Product Sync
              </Text>
              {loading ? (
                <InlineStack gap="200" blockAlign="center">
                  <Spinner size="small" accessibilityLabel="Loading product sync settings" />
                  <Text as="span" tone="subdued">Loading sync settings…</Text>
                </InlineStack>
              ) : (
                <BlockStack gap="300">
                  <Checkbox
                    label={
                      <span>
                        <Tooltip content="You can disable this at any time or remove products from sync in Genseo under Profile > Products.">
                          <span className="product-sync-tooltip-trigger">
                            Sync Shopify products
                          </span>
                        </Tooltip>{' '}
                        with Genseo
                      </span>
                    }
                    checked={productSyncEnabled}
                    onChange={(checked) => {
                      trackAnalyticsEvent('product_sync_preference_changed', {
                        route: '/settings',
                        shopDomain: shop,
                        productSyncEnabled: checked,
                        source: 'settings_checkbox',
                      });
                      setProductSyncEnabled(checked);
                    }}
                    disabled={saving || syncing}
                  />
                  <Text as="p" tone="subdued">
                    When enabled, product create, update, delete and status changes are synced to Genseo automatically.
                  </Text>
                  <InlineStack gap="300">
                    <Text as="span" tone="subdued">Status: {productSyncStatus}</Text>
                    {lastProductSyncAt && <Text as="span" tone="subdued">Last full sync: {new Date(lastProductSyncAt).toLocaleString()}</Text>}
                    {lastProductWebhookAt && <Text as="span" tone="subdued">Last webhook: {new Date(lastProductWebhookAt).toLocaleString()}</Text>}
                  </InlineStack>
                  {productSyncError && (
                    <Banner tone="critical">
                      <p>{productSyncError}</p>
                    </Banner>
                  )}
                  <Button onClick={handleSave} loading={saving || syncing} disabled={saving || syncing}>
                    {productSyncEnabled && !initialProductSyncEnabled ? 'Save & sync products' : 'Save product sync settings'}
                  </Button>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">
                Shop-Informationen
              </Text>
              <BlockStack gap="200">
                <InlineStack>
                  <Text as="p">
                    <strong>Shop:</strong> {shop || 'Nicht verfügbar'}
                  </Text>
                </InlineStack>
                <InlineStack>
                  <Text as="p">
                    <strong>Host:</strong> {host || 'Nicht verfügbar'}
                  </Text>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default SettingsPage;
